/**
 * Every string a visitor can read, in both languages.
 *
 * One dictionary rather than two component trees: a second tree drifts the
 * moment somebody edits one language and forgets the other, and the drift is
 * invisible until a native reader finds it. Here the type makes a missing
 * Arabic string a compile error.
 *
 * **The English copy is the approved copy** and is reproduced exactly as the
 * design package's marketing template states it. It is not paraphrased, and a
 * change to it is a change to the design direction rather than to this file.
 *
 * **The Arabic copy has not been reviewed by a native speaker.** It is written
 * for Saudi merchants rather than translated word for word — `DESIGN.md` is
 * explicit that Arabic is first-class and not a translation variant — but it is
 * a first pass and is listed in the pull request for human review. Do not treat
 * it as approved until somebody who reads Arabic has said so.
 */

export const LANGUAGES = ['en', 'ar'] as const;
export type Language = (typeof LANGUAGES)[number];

export interface Copy {
  readonly documentTitle: string;
  readonly documentDescription: string;
  readonly skipToContent: string;

  readonly nav: {
    readonly home: string;
    readonly joinWaitlist: string;
    /** The workspace entry: Sign in when anonymous, Dashboard when not. */
    readonly signIn: string;
    readonly dashboard: string;
    readonly howItWorks: string;
    readonly switchLanguage: string;
    readonly openMenu: string;
    readonly closeMenu: string;
    /** The label of the *other* language, which is what the toggle shows. */
    readonly otherLanguageName: string;
    readonly sections: string;
    readonly section: (index: number, count: number) => string;
    readonly openWorkspace: string;
  };

  readonly hero: {
    readonly eyebrow: string;
    readonly headline: string;
    readonly support: string;
    readonly landmark: string;
    readonly videoLabel: string;
    readonly startsWith: string;
    readonly yourBusiness: string;
    readonly madeFor: string;
    readonly channels: string;
    readonly generatedExample: string;
    readonly videoTitle: string;
    readonly videoMeta: string;
    /** Three short truths under the composer; never a metric. */
    readonly proof: readonly string[];
  };

  readonly composer: {
    readonly urlLabel: string;
    readonly urlPlaceholder: string;
    readonly promptPlaceholder: string;
    readonly referencePlaceholder: string;
    readonly addLink: string;
    readonly addPrompt: string;
    readonly addReference: string;
    readonly aspectRatio: string;
    readonly linkHint: string;
    readonly promptHint: string;
    readonly referenceHint: string;
    readonly promptAdded: string;
    readonly promptRemoved: string;
    readonly promptExists: string;
    readonly promptChip: string;
    readonly editPrompt: string;
    readonly removePrompt: string;
    readonly addedInputs: string;
    readonly composerRegion: string;
    readonly videoInputs: string;
    readonly productAdded: string;
    readonly referenceAdded: string;
    readonly sourceRemovedPlain: string;
    readonly productSource: string;
    readonly referenceSource: string;
    readonly website: string;
    readonly instagram: string;
    readonly tiktok: string;
    readonly promptNeedsProduct: string;
    readonly errorNotALink: string;
    readonly errorUnsupported: string;
    readonly errorAlreadyAttached: string;
    readonly errorWebsiteOccupied: string;
    readonly errorTikTokOccupied: string;
    readonly errorInstagramOccupied: string;
    readonly storageRequired: string;
    readonly progressUnavailable: string;
    readonly generationFailed: string;
    readonly merchantAccountRequired: string;
    readonly completionRequired: string;
    readonly profileUnavailable: string;
    readonly profileActionFailed: string;
    readonly linkAdded: string;
    readonly noteLabel: string;
    readonly notePlaceholder: string;
    readonly submit: string;
    readonly submitting: string;
    readonly nextStep: string;
    readonly removeContext: (label: string) => string;
    readonly sourceAdded: (label: string) => string;
    readonly sourceRemoved: (label: string) => string;
    readonly attachedCount: (count: number) => string;
    readonly urlExamples: readonly string[];
    /** Example intents shown in the empty composer, one at a time. Written
        for the market, not translated from each other. */
    readonly examplePrompts: readonly string[];
  };

  readonly merchantAuth: {
    readonly claimTitle: string;
    readonly claimBody: string;
    readonly correctTitle: string;
    readonly correctBody: string;
    readonly close: string;
    readonly google: string;
    readonly apple: string;
    readonly loading: string;
    readonly unavailable: string;
  };

  readonly process: {
    readonly heading: string;
    readonly lead: string;
    readonly steps: readonly (readonly [string, string])[];
  };

  readonly story: {
    readonly eyebrow: string;
    readonly heading: string;
    readonly lead: string;
    readonly taste: string;
    readonly direction: string;
    readonly delivered: string;
    readonly sampleTitle: string;
    readonly sampleCaption: string;
    readonly sourceEngagement: string;
    readonly views: string;
    readonly likes: string;
    readonly comments: string;
    readonly dislike: string;
    readonly liked: string;
    readonly tasteNote: string;
    readonly recommended: string;
    readonly directionTitle: string;
    readonly directionHook: string;
    readonly hook: string;
    readonly treatment: string;
    readonly message: string;
    readonly directionTreatment: string;
    readonly directionMessage: string;
    readonly lineTitle: string;
    readonly active: string;
    readonly lineMeta: string;
    readonly deliveries: readonly (readonly [string, string])[];
    readonly deliveryMeta: string;
    readonly lineNote: string;
    readonly disclaimer: string;
  };

  readonly waitlist: {
    readonly eyebrow: string;
    readonly heading: string;
    readonly supporting: string;
    readonly emailLabel: string;
    readonly emailPlaceholder: string;
    readonly urlLabel: string;
    readonly urlPlaceholder: string;
    readonly optional: string;
    readonly submit: string;
    readonly submitting: string;
    readonly footnote: string;
    readonly successTitle: string;
    readonly successBody: string;
    readonly errorEmailMissing: string;
    readonly errorEmailMalformed: string;
    readonly errorRejected: string;
    readonly errorNetwork: string;
    readonly errorRateLimited: string;
    /** Rendered off-screen. A person never sees it, so it is never translated
        in a way that would make it look like a real field. */
    readonly honeypotLabel: string;
  };

  readonly footer: {
    readonly landmark: string;
    readonly tagline: string;
    readonly location: string;
    readonly product: string;
    readonly howItWorks: string;
    readonly story: string;
    readonly waitlist: string;
    readonly account: string;
    readonly rights: (year: number) => string;
    readonly made: string;
  };
}

const en: Copy = {
  documentTitle: 'Mouja — Saudi trends move fast',
  documentDescription:
    'Turn Saudi trends into short-form AI videos for your brand with Mouja.',
  skipToContent: 'Skip to content',

  nav: {
    home: 'mouja.ai, back to top',
    joinWaitlist: 'Join waitlist',
    signIn: 'Sign in',
    dashboard: 'Dashboard',
    howItWorks: 'How it works',
    switchLanguage: 'Switch to Arabic',
    openMenu: 'Open menu',
    closeMenu: 'Close menu',
    otherLanguageName: 'العربية',
    sections: 'Page sections',
    section: (index: number, count: number) => `Section ${index} of ${count}`,
    openWorkspace: 'Open workspace',
  },

  hero: {
    eyebrow: 'AI video for Saudi brands',
    headline: 'Saudi trends move fast. Your brand should too.',
    support:
      'Paste your website. Mouja reads your business, catches the right Saudi trend and delivers a vertical video ready to post.',
    landmark: 'Introduction',
    videoLabel: 'Example vertical video generated with Mouja',
    startsWith: 'Starts with',
    yourBusiness: 'Your business',
    madeFor: 'Made for',
    channels: 'Reels · TikTok · Shorts',
    generatedExample: 'AI-generated example',
    videoTitle: 'Website to vertical video',
    videoMeta: '12 seconds · ready to post',
    proof: ['Built for Saudi merchants', 'Arabic first', '9:16 only'],
  },

  composer: {
    urlLabel: 'Describe the video you want to create or paste a source URL',
    urlPlaceholder: 'Turn my brand into a scroll-stopping TikTok…',
    examplePrompts: [
      'Turn my new oud into a scroll-stopping Reel',
      'A TikTok for our Eid gift bundle',
      'Introduce my store to people in Riyadh',
      'Show how our abaya looks in daylight, no faces',
      'One video a day for my ramadan sale',
    ],
    promptPlaceholder: 'Add a hook, key message or creative direction',
    referencePlaceholder: 'Paste a TikTok or Instagram link',
    addLink: 'Add link',
    addPrompt: 'Add prompt',
    addReference: 'Add reference',
    aspectRatio: 'Video aspect ratio: 9:16',
    linkHint:
      'Add your website to continue. Instagram, TikTok, and a prompt are optional.',
    promptHint: 'Type a hook, message or creative direction, then press Enter.',
    referenceHint: 'Paste your business’s Instagram or TikTok profile URL.',
    promptAdded: 'Prompt added.',
    promptRemoved: 'Prompt removed.',
    promptExists: 'A prompt is already added. Edit or remove it before adding another.',
    promptChip: 'Prompt',
    editPrompt: 'Edit prompt',
    removePrompt: 'Remove prompt',
    addedInputs: 'Added inputs',
    composerRegion: 'AI video composer',
    videoInputs: 'Video inputs',
    productAdded: 'Product source added. Your prompt is unchanged.',
    referenceAdded: 'Business social profile added. Your prompt is unchanged.',
    sourceRemovedPlain: 'Source removed.',
    productSource: 'Business website',
    referenceSource: 'Business social profile',
    website: 'Website',
    instagram: 'Instagram',
    tiktok: 'TikTok',
    promptNeedsProduct: 'Add your business website to continue.',
    errorNotALink:
      'That is not a valid business source. Use a website or a public Instagram or TikTok profile URL.',
    errorUnsupported:
      'That link type is not supported. Use a website or a public Instagram or TikTok profile URL.',
    errorAlreadyAttached: 'That source is already added.',
    errorWebsiteOccupied:
      'A website source is already added. Remove it before adding another.',
    errorTikTokOccupied:
      'A business social profile is already added. Remove it before adding another.',
    errorInstagramOccupied:
      'A business social profile is already added. Remove it before adding another.',
    storageRequired: 'Your browser must allow local storage for this pilot.',
    progressUnavailable: 'Profile progress is unavailable.',
    generationFailed: 'Profile generation could not start.',
    merchantAccountRequired: 'Sign in with a merchant account to claim this profile.',
    completionRequired: 'Choose “Something’s not right” to complete the profile.',
    profileUnavailable: 'The claimed Business Profile is no longer available.',
    profileActionFailed: 'The profile action failed.',
    linkAdded: 'Source added.',
    noteLabel: 'Add a note (optional)',
    notePlaceholder:
      'Anything Mouja should know — a category, a launch, or a product to prioritise.',
    submit: 'Create videos →',
    submitting: 'Starting…',
    nextStep: 'Type a prompt, or paste a website, TikTok or Instagram link.',
    removeContext: (label) => `Remove ${label}`,
    sourceAdded: (label) => `${label} added.`,
    sourceRemoved: (label) => `${label} removed.`,
    attachedCount: (count) =>
      count === 0
        ? 'Press Enter to attach each link'
        : count === 1
          ? '1 link attached'
          : `${String(count)} links attached`,
    urlExamples: [
      'https://mystore.sa',
      'instagram.com/mystore',
      'tiktok.com/@mystore',
      'mystore.sa/products/oud-perfume',
    ],
  },

  merchantAuth: {
    claimTitle: 'Sign in to claim this profile',
    claimBody:
      'Signing in saves this profile and your brief to your Mouja account. Nothing is published yet.',
    correctTitle: 'Sign in to correct this profile',
    correctBody:
      'Signing in lets you edit the identity, offers and geography before Mouja continues with your brief.',
    close: 'Close',
    google: 'Continue with Google',
    apple: 'Continue with Apple',
    loading: 'Loading sign-in options…',
    unavailable: 'Sign-in is temporarily unavailable.',
  },

  process: {
    heading: 'Your business in. A 9:16 video out.',
    lead: 'The product story stays simple because the hero already shows the result.',
    steps: [
      ['Add your context', 'A website, Instagram, TikTok, or one useful note.'],
      ['Catch the right signal', 'Mouja finds the Saudi trend that fits your brand.'],
      ['Create the video', 'A vertical output shaped for short-form channels.'],
    ],
  },

  story: {
    eyebrow: 'From taste to a Production Line',
    heading: 'Your taste becomes a direction. The direction becomes content.',
    lead: 'The same three moments inside the product: what you liked, what Mouja recommends because of it, and what a live Production Line has delivered.',
    taste: 'Taste',
    direction: 'Direction',
    delivered: 'Delivered',
    sampleTitle: 'A video you calibrated on',
    sampleCaption:
      '“Two spoons of oud, one small burner. Warm it slowly — never let it smoke.”',
    sourceEngagement: 'Source engagement',
    views: 'Views',
    likes: 'Likes',
    comments: 'Comments',
    dislike: 'Dislike',
    liked: 'Liked',
    tasteNote:
      'Your taste decisions are personal preference. They never touch the numbers on the source post.',
    recommended: 'Recommended direction',
    directionTitle: 'Twelve millilitres, three airports',
    directionHook: '“It clears security. It still smells like the shop.”',
    hook: 'Hook',
    treatment: 'Treatment',
    message: 'Message',
    directionTreatment:
      'One unbroken take, hands only, daylight on a hotel desk. Arabic captions burned in, no voice-over.',
    directionMessage:
      'The 12 ml travel oud goes in the cabin bag and comes home still full.',
    lineTitle: 'An active Production Line',
    active: 'Active',
    lineMeta: 'Sell more — Travel oud 12 ml · 1 video per day',
    deliveries: [
      ['Vertical video · 22s', 'Ready'],
      ['Vertical video · 19s', 'Delivered'],
      ['Vertical video · 24s', 'Delivered'],
    ],
    deliveryMeta: 'Arabic captions · post copy included',
    lineNote: 'This runs on Mouja’s servers. Closing the tab does not stop the work.',
    disclaimer:
      'Example content shown to illustrate the interface. Not a customer result.',
  },

  waitlist: {
    eyebrow: 'Pre-launch',
    heading: 'Catch the next wave early.',
    supporting:
      'Join the Mouja waitlist and get early access as we open the platform to Saudi merchants.',
    emailLabel: 'Email address',
    emailPlaceholder: 'you@yourstore.sa',
    urlLabel: 'Business or store URL',
    urlPlaceholder: 'yourstore.sa',
    optional: 'optional',
    submit: 'Join the waitlist',
    submitting: 'Joining…',
    footnote: '',
    successTitle: 'You’re on the waitlist.',
    successBody:
      'We’ll email you when Mouja opens to your business. Nothing else happens until then.',
    errorEmailMissing: 'An email address, and you are done.',
    errorEmailMalformed: 'That email does not look right.',
    errorRejected: 'That did not go through. Check the details and try again.',
    errorNetwork: 'That did not reach us. Check your connection and try again.',
    errorRateLimited: 'Too many attempts just now. Wait a moment and try again.',
    honeypotLabel: 'Leave this field empty',
  },

  footer: {
    landmark: 'Footer',
    tagline:
      'AI video for Saudi brands. From your business to a 9:16 video, in Arabic first.',
    location: 'Riyadh, Saudi Arabia',
    product: 'Product',
    howItWorks: 'How it works',
    story: 'How Mouja learns your business',
    waitlist: 'Waitlist',
    account: 'Account',
    rights: (year: number) => `© ${year} Mouja. All rights reserved.`,
    made: 'Made in Riyadh · موجة',
  },
};

const ar: Copy = {
  documentTitle: 'موجة — الترندات السعودية ما تنتظر أحد',
  documentDescription:
    'خلّ موجة تحوّل الترندات السعودية إلى فيديوهات قصيرة لعلامتك بالذكاء الاصطناعي.',
  skipToContent: 'روح للمحتوى',

  nav: {
    home: 'mouja.ai، ارجع لفوق',
    joinWaitlist: 'سجّل بالقائمة',
    signIn: 'سجّل دخولك',
    dashboard: 'لوحة العمل',
    howItWorks: 'كيف تشتغل موجة',
    switchLanguage: 'غيّر اللغة إلى الإنجليزية',
    openMenu: 'افتح القائمة',
    closeMenu: 'سكّر القائمة',
    otherLanguageName: 'English',
    sections: 'أقسام الصفحة',
    section: (index: number, count: number) => `القسم ${index} من ${count}`,
    openWorkspace: 'افتح مساحتك',
  },

  hero: {
    eyebrow: 'فيديو بالذكاء الاصطناعي للبراندات السعودية',
    headline: 'خلي محتواك يلاحق الترندات عنك.',
    support:
      'ألصق موقعك. تقرأ موجة نشاطك، وتلقط الترند السعودي المناسب، وتسلّمك فيديو عمودي جاهز للنشر.',
    landmark: 'المقدمة',
    videoLabel: 'مثال فيديو عمودي سوّته موجة',
    startsWith: 'يبدأ من',
    yourBusiness: 'نشاطك',
    madeFor: 'مسوّى لـ',
    channels: 'Reels · TikTok · Shorts',
    generatedExample: 'مثال مسوّى بالذكاء الاصطناعي',
    videoTitle: 'من الموقع إلى فيديو عمودي',
    videoMeta: '12 ثانية · جاهز للنشر',
    proof: ['مسوّى للتجار السعوديين', 'العربية أول', '9:16 بس'],
  },

  composer: {
    urlLabel: 'اكتب وش الفيديو اللي تبيه أو الصق رابط مصدر',
    urlPlaceholder: 'حوّل براندي إلى فيديو TikTok يوقّف الناس عن التمرير…',
    examplePrompts: [
      'أبي ريلز يعرّف الناس بعودي الجديد',
      'فيديو تيك توك لعرض العيد على الأطقم',
      'عرّف أهل الرياض على متجري',
      'ورّهم شكل العباية في ضوء النهار، بدون وجوه',
      'فيديو كل يوم لتخفيضات رمضان',
    ],
    promptPlaceholder: 'اكتب افتتاحية أو رسالة أو توجيه إبداعي',
    referencePlaceholder: 'الصق رابط TikTok أو Instagram',
    addLink: 'أضف رابط',
    addPrompt: 'أضف وصف',
    addReference: 'أضف مرجع',
    aspectRatio: 'نسبة أبعاد الفيديو: 9:16',
    linkHint: 'ضيف موقع نشاطك عشان نكمل. Instagram وTikTok والوصف اختياريين.',
    promptHint: 'اكتب افتتاحية أو رسالة أو توجيه إبداعي، وبعدين اضغط Enter.',
    referenceHint: 'الصق رابط حساب نشاطك على Instagram أو TikTok.',
    promptAdded: 'انضاف الوصف.',
    promptRemoved: 'انشال الوصف.',
    promptExists: 'فيه وصف مضاف من قبل. عدّله أو شِله قبل ما تضيف غيره.',
    promptChip: 'الوصف',
    editPrompt: 'عدّل الوصف',
    removePrompt: 'شِل الوصف',
    addedInputs: 'اللي أضفته',
    composerRegion: 'أداة صناعة الفيديو بالذكاء الاصطناعي',
    videoInputs: 'مدخلات الفيديو',
    productAdded: 'انضاف موقع نشاطك. وصفك باقي زي ما هو.',
    referenceAdded: 'انضاف حساب نشاطك. وصفك باقي زي ما هو.',
    sourceRemovedPlain: 'انشال المصدر.',
    productSource: 'موقع النشاط',
    referenceSource: 'حساب النشاط',
    website: 'الموقع',
    instagram: 'Instagram',
    tiktok: 'TikTok',
    promptNeedsProduct: 'ضيف موقع نشاطك عشان نكمل.',
    errorNotALink:
      'هذا مو مصدر صالح لنشاطك. استخدم رابط موقع أو حساب Instagram أو TikTok عام.',
    errorUnsupported:
      'نوع الرابط مو مدعوم. استخدم رابط موقع أو حساب Instagram أو TikTok عام.',
    errorAlreadyAttached: 'هذا المصدر مضاف من قبل.',
    errorWebsiteOccupied: 'فيه مصدر موقع مضاف من قبل. شِله قبل ما تضيف غيره.',
    errorTikTokOccupied: 'فيه حساب لنشاطك مضاف من قبل. شِله قبل ما تضيف غيره.',
    errorInstagramOccupied: 'فيه حساب لنشاطك مضاف من قبل. شِله قبل ما تضيف غيره.',
    storageRequired: 'لازم متصفحك يسمح بالتخزين المحلي عشان تكمل.',
    progressUnavailable: 'ما قدرنا نعرض تقدّم بناء الملف.',
    generationFailed: 'ما قدرنا نبدأ ببناء ملفك التجاري.',
    merchantAccountRequired: 'سجّل دخولك بحساب تاجر عشان يصير الملف لك.',
    completionRequired: 'اختر «فيه شي مو صحيح» عشان تكمّل الملف.',
    profileUnavailable: 'الملف التجاري اللي تملّكته ما عاد متاح.',
    profileActionFailed: 'ما قدرنا ننفّذ هذا على الملف.',
    linkAdded: 'انضاف المصدر.',
    noteLabel: 'أضف ملاحظة (اختياري)',
    notePlaceholder: 'أي شي تبي موجة تعرفه — فئة، إطلاق، أو منتج له أولوية.',
    submit: 'سوّ الفيديوهات ←',
    submitting: 'نبدأ…',
    nextStep: 'اكتب وصف، أو الصق رابط موقعك أو TikTok أو Instagram.',
    removeContext: (label) => `شِل ${label}`,
    sourceAdded: (label) => `انضاف ${label}.`,
    sourceRemoved: (label) => `انشال ${label}.`,
    attachedCount: (count) =>
      count === 0
        ? 'اضغط Enter عشان تضيف كل رابط'
        : count === 1
          ? 'رابط واحد مضاف'
          : `${String(count)} روابط مضافة`,
    urlExamples: [
      'https://mystore.sa',
      'instagram.com/mystore',
      'tiktok.com/@mystore',
      'mystore.sa/products/oud-perfume',
    ],
  },

  merchantAuth: {
    claimTitle: 'سجّل دخولك عشان يصير الملف لك',
    claimBody: 'تسجيل الدخول يحفظ الملف وموجزك في حسابك على موجة. ما انتشر شي للحين.',
    correctTitle: 'سجّل دخولك عشان تصحّح الملف',
    correctBody:
      'تسجيل الدخول يخلّيك تعدّل الهوية والعروض والموقع قبل ما تكمّل موجة موجزك.',
    close: 'سكّر',
    google: 'كمّل بحساب Google',
    apple: 'كمّل بحساب Apple',
    loading: 'نجهّز خيارات تسجيل الدخول…',
    unavailable: 'تسجيل الدخول مو متاح الحين.',
  },

  process: {
    heading: 'نشاطك أول. وبعدها فيديو 9:16.',
    lead: 'القصة بسيطة، لأن النتيجة تشوفها من أول شاشة.',
    steps: [
      ['عطنا سياق نشاطك', 'موقع، أو Instagram، أو TikTok، أو ملاحظة مفيدة.'],
      ['التقط الإشارة الصح', 'موجة تلقى الترند السعودي اللي يناسب براندك.'],
      ['سوّ الفيديو', 'فيديو عمودي جاهز لقنوات المقاطع القصيرة.'],
    ],
  },

  story: {
    eyebrow: 'من الذوق إلى خط إنتاج',
    heading: 'ذوقك يصبح اتجاهًا، والاتجاه يصبح محتوى.',
    lead: 'اللحظات الثلاث نفسها داخل المنتج: ما أعجبك، وما توصي به موجة بسببه، وما سلّمه خط إنتاج نشِط.',
    taste: 'الذوق',
    direction: 'الاتجاه',
    delivered: 'المُسلَّم',
    sampleTitle: 'فيديو عايرت ذوقك عليه',
    sampleCaption:
      '«ملعقتان من العود، ومبخرة صغيرة واحدة. دفّئه على مهل — ولا تدعه يُدخّن.»',
    sourceEngagement: 'تفاعل المصدر',
    views: 'المشاهدات',
    likes: 'الإعجابات',
    comments: 'التعليقات',
    dislike: 'لم يعجبني',
    liked: 'أعجبني',
    tasteNote: 'قراراتك الذوقية تفضيل شخصي، ولا تمسّ أرقام المنشور الأصلي.',
    recommended: 'الاتجاه الموصى به',
    directionTitle: 'اثنا عشر مليلترًا، وثلاثة مطارات',
    directionHook: '«يمرّ من التفتيش، ويبقى برائحة المتجر.»',
    hook: 'الجملة الافتتاحية',
    treatment: 'المعالجة البصرية',
    message: 'الرسالة',
    directionTreatment:
      'مشهد واحد متصل، أيدٍ فقط، ضوء نهار على مكتب فندق. ترجمة عربية مدمجة، وبلا تعليق صوتي.',
    directionMessage: 'عود السفر ١٢ مل يذهب في حقيبة الطائرة، ويعود وما زال ممتلئًا.',
    lineTitle: 'خط إنتاج نشِط',
    active: 'نشِط',
    lineMeta: 'بيع أكثر — عود السفر ١٢ مل · فيديو واحد يوميًا',
    deliveries: [
      ['فيديو رأسي · ٢٢ ثانية', 'جاهز'],
      ['فيديو رأسي · ١٩ ثانية', 'سُلِّم'],
      ['فيديو رأسي · ٢٤ ثانية', 'سُلِّم'],
    ],
    deliveryMeta: 'ترجمة عربية · مع نص المنشور',
    lineNote: 'يعمل هذا على خوادم موجة، وإغلاق الصفحة لا يوقف العمل.',
    disclaimer: 'محتوى توضيحي لعرض الواجهة، وليس نتيجة عميل.',
  },

  waitlist: {
    eyebrow: 'قبل الإطلاق',
    heading: 'الحق الموجة الجاية من بدري.',
    supporting:
      'سجّل في قائمة انتظار موجة وخذ وصول مبكر أول ما نفتح المنصة للتجار في السعودية.',
    emailLabel: 'البريد الإلكتروني',
    emailPlaceholder: 'you@yourstore.sa',
    urlLabel: 'رابط متجرك أو نشاطك',
    urlPlaceholder: 'yourstore.sa',
    optional: 'اختياري',
    submit: 'سجّلني في القائمة',
    submitting: 'نسجّلك…',
    footnote: '',
    successTitle: 'تم، أنت في القائمة.',
    successBody: 'بنتواصل معك أول ما يجهز وصولك.',
    errorEmailMissing: 'بس بريدك الإلكتروني وخلاص.',
    errorEmailMalformed: 'شكل البريد مو صحيح.',
    errorRejected: 'ما اكتمل الإرسال. راجع البيانات وجرّب مرة ثانية.',
    errorNetwork: 'ما وصلنا طلبك. تأكد من اتصالك وجرّب مرة ثانية.',
    errorRateLimited: 'محاولات كثيرة الحين. انتظر شوي وجرّب مرة ثانية.',
    honeypotLabel: 'خلّ هذا الحقل فاضي',
  },

  footer: {
    landmark: 'تذييل الصفحة',
    tagline:
      'فيديو بالذكاء الاصطناعي للبراندات السعودية. من نشاطك إلى فيديو 9:16، بالعربية أول.',
    location: 'الرياض، السعودية',
    product: 'المنتج',
    howItWorks: 'كيف تشتغل موجة',
    story: 'كيف تتعلّم موجة نشاطك',
    waitlist: 'قائمة الانتظار',
    account: 'الحساب',
    rights: (year: number) => `© ${year} موجة. كل الحقوق محفوظة.`,
    made: 'مسوّى في الرياض · Mouja',
  },
};

export const COPY: Readonly<Record<Language, Copy>> = { en, ar };

/** The `dir` a language is written in. */
export function directionFor(language: Language): 'ltr' | 'rtl' {
  return language === 'ar' ? 'rtl' : 'ltr';
}
